import { Box, Text, ThemeSwitcher } from "@okshaun/components";
import { css } from "@styled-system/css";
import type { ReactNode } from "react";

interface NavLink {
  href: string;
  label: string;
  popOut?: boolean;
}

const NAV_LINKS: NavLink[] = [
  { href: "/", label: "Overview" },
  { href: "/triage", label: "Triage" },
  { href: "/gantt", label: "Gantt" },
  { href: "/memories", label: "Memories" },
  { href: "https://music.sfox.work", label: "Music", popOut: true },
  { href: "https://files.sfox.work", label: "Files", popOut: true },
  { href: "https://photos.sfox.work", label: "Photos", popOut: true },
];

interface DashboardHeaderProps {
  gridArea?: string;
  /** Optional content placed before the theme switcher (e.g. a freshness stamp). */
  trailing?: ReactNode;
}

/**
 * Nav link states: rest subtle, hover full text, current page bold with the
 * purple rule, keyboard focus ring. A plain <a> takes classes, not style props.
 */
const navLink = css({
  textStyle: "mono.xs",
  textDecoration: "none",
  color: "text.subtle",
  pb: "2",
  borderBottom: "[1px solid transparent]",
  outline: "none",
  transitionProperty: "[color, border-color]",
  transitionDuration: "fast",
  _hover: { color: "text" },
  _focusVisible: { color: "text", borderBottomColor: "border.focused" },
  "&[aria-current=page]": { color: "text.bold", borderBottomColor: "text.accent.purple" },
});

export function DashboardHeader({ gridArea, trailing }: DashboardHeaderProps) {
  const currentPath = window.location.pathname;

  const isActive = (href: string) =>
    href === "/" ? currentPath === "/" || currentPath === "" : currentPath === href;

  return (
    <Box
      as="header"
      gridArea={gridArea}
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      columnGap="24"
      rowGap="8"
    >
      <Text textStyle="mono.sm" color="text.accent.purple">
        sfox.work
      </Text>

      <Box
        as="nav"
        aria-label="Dashboard navigation"
        display="flex"
        flexWrap="wrap"
        columnGap="16"
        rowGap="4"
      >
        {NAV_LINKS.map(({ href, label, popOut }) => {
          const active = isActive(href);
          return (
            <a
              key={href}
              href={href}
              aria-current={active ? "page" : undefined}
              target={popOut ? "_blank" : undefined}
              rel={popOut ? "noreferrer" : undefined}
              className={navLink}
            >
              {label}
            </a>
          );
        })}
      </Box>

      <Box ml="auto" display="flex" alignItems="center" gap="16">
        {trailing}
        <ThemeSwitcher />
      </Box>
    </Box>
  );
}
